import { Modal, Table } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import _, { isEmpty } from "lodash";
import { FC, Fragment, useEffect, useState } from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { getDDMMYYYFormat, getHHmmFormat } from "../../../helper/dateTimeHelper";
import moduleConfig from "../../../module.config";
import { IInputProps, IUseFormStructureItem } from "../../../modules/form";
import { ClassNames, ObjectUtils } from "../../../modules/utils";
import { FileAttachmentCard } from "../../../pages/procedure/upload";
import FontFa from "../../fontFa/fontFa";
import { Icon } from "../../icon";
import { ImagePreview } from "../../image-preview";
import ModalNotification from "../../modal-notification";
import { CreateNotification } from "../../notifier";
import DownloadExcelTemplate from "./DownloadExcelTemplate";
import DownloadPassport from "./DownloadPassport";
import UploadFile from "./UploadFile";
import type { ExcelTemplateType } from "./excel-template";
import { InvalidDataType, VALIDATE_IMPORT_FILE_TYPE } from "./excel-template/template/_utils";

export interface IInputArrayDataStructureItem extends IUseFormStructureItem {
  isVisibleOnList?: boolean;
  render?: (rowValue: any) => any;
}

export interface IInputArrayFormProps {
  childFormData: any;
  structure: IInputArrayDataStructureItem[];
  teamList?: string[];
  isOnlyPassport?: boolean;
  isRefund?: boolean;
  isSelectUnit?: number;
  onSubmit: (values: any) => void;
  name: string;
  isUpdateUI: boolean;
  unit?: string;
  phoneNumber?: string;
  // ============================ Events ============================
  onRemove?: (id: any, values: any) => Promise<any>;
  onUpdate?: (id: any, values: any) => Promise<any>;
}

export interface IInputArrayDataProps extends IInputProps {
  isDisableEdit?: boolean;
  generation?: number;
  form: FC<IInputArrayFormProps>;
  structure: IInputArrayDataStructureItem[];
  teamList?: string[];
  isOnlyPassport?: boolean;
  isRefund?: boolean;
  isTrip?: boolean;
  isSelectUnit?: number;
  notifyText?: string;
  isDisabled?: boolean;
  excelTemplate?: ExcelTemplateType;
  exportPassport?: boolean;
  unit?: string;
  phoneNumber?: string;
  // ============================ Events ============================
  onCreate?: (values: any) => Promise<any>;
  onRemove?: (id: any, values: any) => Promise<any>;
  onUpdate?: (id: any, values: any) => Promise<any>;
}

export const InputArrayDataV2: FC<IInputArrayDataProps> = (props) => {
  const [disabledUpload, setDisabledUpload] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formData, setFormData] = useState(null) as any;
  const data = Array.isArray(props.value)
    ? props.value.map((item, index) => ({ ...item, id: item.id || index + 1 }))
    : [];
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectBox, setIsSelectBox] = useState(false);
  const [checkedList, setCheckedList] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");

  const childStructureVisible = props.structure.filter((v) => v.isVisibleOnList || v.render);
  const childFormId = `InputArrayData__Form__${props.name}`;
  const isUpdateUI = !!formData;

  const normalizeValueList = (value: any) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "undefined" || value === null) return [];
    return [value];
  };

  const withGeneratedIds = (items: any[], startingId: number) => {
    return items.map((item, index) => ({
      ...item,
      id: item.id || startingId + index,
    }));
  };

  useEffect(() => {
    if (isFormVisible) {
      document.onkeyup = function (e) {
        if (e.key === "Escape") closeForm();
      };
    }
  }, [isFormVisible]);

  const closeForm = () => {
    setIsFormVisible(false);
    setFormData(null);
  };

  const isImageFile = (str: string) =>
    typeof str === "string" && str?.includes("https://viagsstorage.blob.core.windows.net/viags-file/ImageSmall");

  const isDocumentFile = (fileUrl: string) =>
    typeof fileUrl === "string" && fileUrl?.includes("https://viagsstorage.blob.core.windows.net/viags-file/file");

  const isGrantedArea = (listArea: any[]) => {
    return Array.isArray(listArea);
  };

  const isImageArray = (imagesUrl: string[]) => {
    return Array.isArray(imagesUrl) && imagesUrl.every((s) => isImageFile(s));
  };

  const copyItem = (value: any) => {
    const newData = _.cloneDeep(data);
    const newValue = { ...value, id: data.length + 2 };
    newData.push(newValue);
    props.onChange(newData);
  };

  const removeItem = async (id: any, values: any) => {
    setIsLoading(true);
    try {
      if (props.onRemove) await props.onRemove(id, values);
      const newData = data.filter((v) => v.id !== id);
      props.onChange(newData);
      setDisabledUpload(!isEmpty(newData));
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const handleDeleteSelection = () => {
    const newCheckList = _.differenceWith(data, checkedList, _.isEqual);
    props.onChange(newCheckList);
    setCheckedList([]);
    setIsSelectBox(false);
    CreateNotification({
      title: "Thành công",
      body: "Xóa các dữ liệu đã chọn thành công!",
      icon: "/icons/success.png",
    });
    return;
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    handleDeleteSelection();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleUploadFile = (data: unknown[]) => {
    props.onChange(data);
  };

  const isInvalidField = (field: any) => {
    return (
      typeof field === "object" &&
      !Array.isArray(field) &&
      field !== null &&
      "type" in field &&
      field.type === VALIDATE_IMPORT_FILE_TYPE
    );
  };

  const renderCellValue = (childValue: any, column: IInputArrayDataStructureItem) => {
    const { name, render, isMutilLocale } = column;

    if (render) return render(childValue);
    if (isMutilLocale) return ObjectUtils.getIn(childValue, `${name}.${moduleConfig.getLocaleList()[0].key}`, "");

    const obj = ObjectUtils.getIn(childValue, name, "");

    if (isDocumentFile(obj)) {
      const listFile: [string] = obj ? JSON.parse(obj) : [];
      return <FileAttachmentCard listFile={listFile}></FileAttachmentCard>;
    }
    if (isImageFile(obj))
      return (
        <PhotoProvider>
          <PhotoView src={obj}>
            <img src={obj} width={100} height={100} alt="" style={{ cursor: "pointer" }}></img>
          </PhotoView>
        </PhotoProvider>
      );
    if (isImageArray(obj)) return <ImagePreview imageUrls={obj} />;
    if (!props.isTrip && isGrantedArea(obj)) {
      const grantedArea = [...childValue?.grantedArea];

      return (
        <div className="d-flex" style={{ width: "200%", maxWidth: "120%" }}>
          {grantedArea.map((e: any) => (
            <div style={{ backgroundColor: e.color, color: "#FFFFFF", padding: "2px" }}>{e.label}</div>
          ))}
        </div>
      );
    }

    if (props.isTrip && isGrantedArea(obj)) {
      const trip: any[] = [...childValue?.TripBusiness];
      return (
        <div>
          {trip.map((e: any) => (
            <div>{`${e.from} - ${e.to} (${getDDMMYYYFormat(e.date)} ${getHHmmFormat(e.date)})`}</div>
          ))}
        </div>
      );
    }

    if (isInvalidField(obj)) {
      const { isValid, text, message } = obj as InvalidDataType;

      if (!isValid) {
        return (
          <span style={{ color: "red" }} title={message}>
            {text ?? ""}
          </span>
        );
      }
    }
    return `${obj ?? ""}`;
  };

  const columns: ColumnsType<any> = [];

  if (!props.isDisableEdit && !isSelectBox) {
    columns.push({
      title: "",
      key: "actions",
      className: "colActions",
      render: (_: any, childValue: any) => (
        <div className="d-flex">
          <div
            className="btnEdit"
            onClick={() => {
              for (const [key, value] of Object.entries(childValue)) {
                if (isInvalidField(value)) {
                  childValue[key] = "";
                }
              }
              setFormData(childValue);
              setIsFormVisible(true);
            }}
          >
            <Icon.Edit />
          </div>

          <div
            className="btnCopy"
            title="Copy thông tin"
            hidden={props.isDisabled}
            onClick={() => copyItem(childValue)}
          >
            <FontFa name="faCopy" color="#11EB3F" />
          </div>

          <div className="btnRemove" onClick={() => removeItem(childValue.id, childValue)}>
            <Icon.Remove />
          </div>
        </div>
      ),
    });
  }

  columns.push({
    title: "STT.",
    dataIndex: "__index__",
    key: "__index__",
    render: (_: any, __: any, index: number) => index + 1,
  });

  columns.push(
    ...childStructureVisible.map((structureItem, index) => ({
      title: structureItem.label,
      dataIndex: structureItem.name,
      key: structureItem.name || `${structureItem.label || "column"}-${index}`,
      render: (_: any, record: any) => renderCellValue(record, structureItem),
    })),
  );

  const rowSelection: TableProps<any>["rowSelection"] =
    !props.isDisableEdit && isSelectBox
      ? {
          selectedRowKeys: checkedList.map((item) => item.id),
          preserveSelectedRowKeys: true,
          onChange: (_selectedRowKeys, selectedRows: any[]) => {
            setCheckedList(selectedRows);
          },
        }
      : undefined;

  return (
    <Fragment>
      <div className={ClassNames({ InputArrayData: true, [`generation-${props.generation || 0}`]: true })}>
        {isLoading ? (
          <div className="tableLoading">
            <Icon.Loading />
          </div>
        ) : null}
        <div className="ctas float-left">
          {!props.isDisabled && (
            <>
              <button
                type="button"
                disabled={props.isDisableEdit}
                className="btnAddMore"
                onClick={() => {
                  if (props.isSelectUnit === 0) {
                    setNotificationMessage(props.notifyText || "Anh/Chị vui lòng chọn CQ/ĐV");
                    setIsNotificationModalOpen(true);
                    return;
                  }
                  setIsFormVisible(true);
                }}
              >
                Thêm mới
              </button>

              {!isSelectBox && (
                <button
                  type="button"
                  disabled={props.isDisableEdit}
                  className="btnDeleteAll"
                  onClick={() => setIsSelectBox(true)}
                >
                  Chọn nhiều để xóa
                </button>
              )}

              {checkedList.length > 0 && (
                <button type="button" disabled={props.isDisableEdit} className="btnConfirmDelete" onClick={showModal}>
                  Xóa dữ liệu đã chọn
                </button>
              )}

              {checkedList.length <= 0 && isSelectBox && (
                <button
                  type="button"
                  disabled={props.isDisableEdit}
                  className="btnBackDelete"
                  onClick={() => setIsSelectBox(false)}
                >
                  Quay lại
                </button>
              )}

              {props.excelTemplate && (
                <>
                  <UploadFile
                    onChange={handleUploadFile}
                    disabled={disabledUpload}
                    template={props.excelTemplate}
                    onClick={(e) => {
                      if (props.isSelectUnit === 0) {
                        setNotificationMessage(props.notifyText || "Anh/Chị vui lòng chọn CQ/ĐV");
                        setIsNotificationModalOpen(true);
                        e.preventDefault();
                      }
                    }}
                  />
                  <DownloadExcelTemplate template={props.excelTemplate} />
                </>
              )}
            </>
          )}
          {props.exportPassport && <DownloadPassport value={props.value} />}
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          pagination={false}
          rowSelection={rowSelection}
          locale={{ emptyText: "" }}
        />
      </div>

      {isFormVisible ? (
        <div
          id={childFormId}
          className={ClassNames({ InputArrayData__Form: true, [`generation-${props.generation || 0}`]: true })}
          // onClick={(e: any) => {
          //     if (e.target.id === childFormId) setIsFormVisible(false);
          // }}
        >
          <div className="wraper">
            <div className="head">
              <div className="title">
                <span onClick={() => closeForm()}>
                  <Icon.ArrowLeft />
                </span>
                {formData ? "Chỉnh sửa" : "Thêm mới"}: {props.label}
              </div>

              <div className="btnClose" onClick={() => closeForm()}>
                <Icon.Close />
              </div>
            </div>

            <div className="body">
              <props.form
                isUpdateUI={isUpdateUI}
                childFormData={formData}
                structure={props.structure}
                teamList={props.teamList}
                isOnlyPassport={props.isOnlyPassport}
                isRefund={props.isRefund}
                onSubmit={async (values) => {
                  if (isUpdateUI) {
                    try {
                      const valueList = normalizeValueList(values);
                      if (!valueList.length) return;

                      try {
                        if (props.onUpdate) await props.onUpdate(formData.id, values);

                        if (formData.id) {
                          props.onChange(
                            data.map((item) => {
                              if (item.id === formData.id) return { ...formData, ...valueList[0] };
                              else return item;
                            }),
                          );
                        } else props.onChange([...data, ...withGeneratedIds(valueList, data.length + 1)]);
                      } catch (error) {
                        console.error(error);
                      }
                      setIsFormVisible(false);
                    } catch (error) {
                      console.error(error);
                    }
                  } else {
                    try {
                      const valueList = normalizeValueList(values);
                      if (!valueList.length) return;

                      try {
                        if (props.onCreate) {
                          const createdItems: any[] = [];
                          for (const item of valueList) {
                            const remoteId = (await props.onCreate(item)) || Date.now();
                            createdItems.push({ ...item, id: remoteId });
                          }
                          props.onChange([...data, ...createdItems]);
                        } else {
                          props.onChange([...data, ...withGeneratedIds(valueList, data.length + 1)]);
                        }
                      } catch (error) {
                        console.error(error);
                      }
                      setIsFormVisible(false);
                    } catch (error) {
                      console.error(error);
                    }
                  }
                }}
                name={props.name}
                onRemove={props.onRemove}
                onUpdate={props.onUpdate}
              />
            </div>
          </div>
        </div>
      ) : null}
      <Modal
        title="Xác nhận"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{ danger: true }}
        cancelText="Đóng"
        okText="Xác nhận"
      >
        <p>Anh/Chị xác nhận muốn xóa các dữ liệu đã chọn</p>
      </Modal>
      <ModalNotification
        open={isNotificationModalOpen}
        type="warning"
        title="Thông báo"
        message={notificationMessage}
        onCancel={() => setIsNotificationModalOpen(false)}
        onOk={() => setIsNotificationModalOpen(false)}
      />
    </Fragment>
  );
};
